/**
 * SuperAdminLayout - Sidebar layout profesional estilo RealtyOS
 */

import { Link, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const Icon = {
  dashboard: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
      <rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
    </svg>
  ),
  companies: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
      <polyline points="9 22 9 12 15 12 15 22"/>
    </svg>
  ),
  users: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
      <circle cx="9" cy="7" r="4"/>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  ),
  shield: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    </svg>
  ),
  stats: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/>
      <line x1="6" y1="20" x2="6" y2="14"/>
    </svg>
  ),
  logout: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
      <polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
    </svg>
  ),
};

export function SuperAdminLayout() {
  const { pathname } = useLocation();
  const { signOut, userData } = useAuth();

  const handleLogout = async () => {
    try { await signOut(); } catch (e) { console.error(e); }
  };

  const navItems = [
    { path: '/super-admin',              label: 'Dashboard',    icon: Icon.dashboard,  exact: true },
    { path: '/super-admin/companies',    label: 'Empresas',     icon: Icon.companies },
    { path: '/super-admin/users',        label: 'Usuarios',     icon: Icon.users },
    { path: '/super-admin/permissions',  label: 'Permisos',     icon: Icon.shield },
    { path: '/super-admin/stats',        label: 'Estadísticas', icon: Icon.stats },
  ];

  const isActive = (path, exact = false) =>
    exact ? pathname === path : pathname.startsWith(path);

  const displayName = userData?.email?.split('@')[0] || 'Super Admin';
  const initials = displayName.slice(0, 1).toUpperCase();

  return (
    <div style={s.root}>
      {/* ── Sidebar ── */}
      <aside style={s.sidebar}>
        {/* Logo DataForge */}
        <div style={s.sidebarHeader}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0 4px' }}>
            <img src="/dataforge_blue_64.png" alt="DataForge" style={{ width: 36, height: 36, borderRadius: 8, objectFit: 'contain' }} />
            <div>
              <div style={s.logoName}>DataForge</div>
              <div style={s.logoSub}>SIGNOS</div>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav style={s.nav}>
          {navItems.map((item) => {
            const active = isActive(item.path, item.exact);
            return (
              <Link
                key={item.path}
                to={item.path}
                style={{
                  ...s.navItem,
                  background: active ? 'rgba(79,108,247,0.18)' : 'transparent',
                  color: active ? '#c5d0f5' : '#6b80b8',
                  borderLeft: active ? '3px solid #4f6cf7' : '3px solid transparent',
                  paddingLeft: active ? 13 : 16,
                }}
                onMouseEnter={(e) => {
                  if (!active) {
                    e.currentTarget.style.background = 'rgba(79,108,247,0.08)';
                    e.currentTarget.style.color = '#a0b0e0';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!active) {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = '#6b80b8';
                  }
                }}
              >
                <span style={{ flexShrink: 0 }}>{item.icon}</span>
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>

        {/* Footer usuario */}
        <div style={s.sidebarFooter}>
          <div style={s.userRow}>
            <div style={s.avatar}>{initials}</div>
            <div style={{ minWidth: 0 }}>
              <div style={s.userName}>{displayName}</div>
              <div style={s.userRole}>Super Admin</div>
            </div>
          </div>
          <button onClick={handleLogout} style={s.logoutBtn} title="Cerrar sesión">
            {Icon.logout}
          </button>
        </div>
      </aside>

      {/* ── Contenido principal ── */}
      <main style={s.main}>
        <div className="light-content">
          <Outlet />
        </div>
      </main>

      <style>{`
        /* ── Área de contenido claro ── */
        .light-content {
          --bg: #ffffff;
          --card: #ffffff;
          --text: #0f172a;
          --muted: #64748b;
          --primary: #eef2ff;
          --primary-strong: #4f46e5;
          color: #0f172a;
          background: #f1f5f9;
        }
        .light-content [style*="#0b1028"],
        .light-content [style*="#0d1330"],
        .light-content [style*="#0d1430"],
        .light-content [style*="#0f1536"],
        .light-content [style*="#080e24"],
        .light-content [style*="#1e293b"],
        .light-content [style*="#0a0f2e"],
        .light-content [style*="#111827"],
        .light-content [style*="#1a1a2e"] {
          background: #ffffff !important;
          color: #0f172a !important;
          border-color: #e2e8f0 !important;
        }
        .light-content .card {
          background: #ffffff !important;
          border: 1px solid #e2e8f0 !important;
          box-shadow: 0 1px 3px rgba(0,0,0,0.06) !important;
          color: #0f172a !important;
        }
        .light-content .title,
        .light-content h1, .light-content h2, .light-content h3, .light-content h4 {
          color: #0f172a !important; font-weight: 600;
        }
        .light-content .kicker, .light-content .meta, .light-content label {
          color: #64748b !important;
        }
        .light-content p { color: #475569 !important; }
        .light-content .btn { background: #1e293b !important; color: #ffffff !important; border: none !important; }
        .light-content .btn:hover { background: #0f172a !important; }
        .light-content .copy { background: #ffffff !important; border: 1px solid #e2e8f0 !important; color: #374151 !important; }
        .light-content .copy:hover { background: #f8fafc !important; }
        .light-content input, .light-content textarea, .light-content select {
          background: #ffffff !important; color: #0f172a !important; border: 1px solid #e2e8f0 !important;
        }
        .light-content input::placeholder, .light-content textarea::placeholder { color: #94a3b8 !important; opacity: 1; }
        .light-content input:focus, .light-content textarea:focus, .light-content select:focus {
          border-color: #1e293b !important; box-shadow: 0 0 0 3px rgba(30,41,59,0.08) !important; outline: none !important;
        }
        .light-content table { color: #0f172a !important; }
        .light-content th { color: #374151 !important; background: #f8fafc !important; border-bottom: 2px solid #e2e8f0 !important; font-weight: 600; }
        .light-content td { border-bottom: 1px solid #f1f5f9 !important; color: #0f172a !important; }
        .light-content tr:hover td { background: #f8fafc !important; }
        .light-content .drop { background: #f8fafc !important; border: 2px dashed #cbd5e1 !important; color: #64748b !important; }
        .light-content .badge { font-weight: 500 !important; font-size: 12px !important; border-radius: 6px !important; padding: 3px 10px !important; }
        .light-content [style*="background: #10b981"], .light-content [style*="background:#10b981"],
        .light-content [style*="background: #22c55e"], .light-content [style*="background:#22c55e"],
        .light-content [style*="background: green"] { background: #dcfce7 !important; color: #166534 !important; border: 1px solid #bbf7d0 !important; }
        .light-content [style*="background: #3b82f6"], .light-content [style*="background:#3b82f6"],
        .light-content [style*="background: #60a5fa"], .light-content [style*="background:#60a5fa"],
        .light-content [style*="background: blue"] { background: #dbeafe !important; color: #1e40af !important; border: 1px solid #bfdbfe !important; }
        .light-content [style*="background: #f59e0b"], .light-content [style*="background:#f59e0b"],
        .light-content [style*="background: #fb923c"], .light-content [style*="background:#fb923c"],
        .light-content [style*="background: orange"] { background: #fef3c7 !important; color: #92400e !important; border: 1px solid #fde68a !important; }
        .light-content [style*="background: #ef4444"], .light-content [style*="background:#ef4444"],
        .light-content [style*="background: red"] { background: #fee2e2 !important; color: #991b1b !important; border: 1px solid #fecaca !important; }
        .light-content [style*="background: #8b5cf6"], .light-content [style*="background:#8b5cf6"],
        .light-content [style*="background: #a78bfa"], .light-content [style*="background:#a78bfa"] { background: #ede9fe !important; color: #5b21b6 !important; border: 1px solid #ddd6fe !important; }
        .light-content [style*="color: #f59e0b"], .light-content [style*="color:#f59e0b"],
        .light-content [style*="color: orange"], .light-content [style*="color: #fb923c"],
        .light-content [style*="color:#fb923c"] { color: #c2410c !important; font-weight: 600 !important; }
      `}</style>
    </div>
  );
}

export default SuperAdminLayout;

const s = {
  root: {
    display: 'flex',
    minHeight: '100vh',
    background: '#0b1028',
  },
  sidebar: {
    width: 240,
    flexShrink: 0,
    background: '#080e24',
    borderRight: '1px solid #151f45',
    display: 'flex',
    flexDirection: 'column',
    position: 'fixed',
    top: 0, left: 0,
    height: '100vh',
    zIndex: 50,
    overflowY: 'auto',
  },
  sidebarHeader: {
    padding: '22px 16px 18px',
    borderBottom: '1px solid #151f45',
    flexShrink: 0,
  },
  logoBox: {
    width: 36, height: 36, borderRadius: 8,
    background: '#1e2d6b',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontWeight: 700, fontSize: 13, color: '#c5d0f5', flexShrink: 0,
  },
  logoName: {
    fontWeight: 700, fontSize: 14, color: '#dde4f8', lineHeight: 1.2,
  },
  logoSub: {
    fontSize: 9, color: '#6b80b8', letterSpacing: 2,
  },
  nav: {
    flex: 1,
    padding: '16px 10px',
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 14px 10px 16px',
    borderRadius: 8,
    textDecoration: 'none',
    fontSize: 13,
    fontWeight: 500,
    transition: 'all 0.15s',
  },
  sidebarFooter: {
    padding: '14px 16px',
    borderTop: '1px solid #151f45',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexShrink: 0,
  },
  userRow: {
    display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0,
  },
  avatar: {
    width: 32, height: 32, borderRadius: '50%',
    background: '#1e2d6b',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontWeight: 700, fontSize: 13, color: '#c5d0f5', flexShrink: 0,
  },
  userName: {
    fontSize: 12, fontWeight: 600, color: '#dde4f8',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  userRole: { fontSize: 10, color: '#6b80b8', marginTop: 1 },
  logoutBtn: {
    background: 'transparent', border: 'none', color: '#6b80b8',
    cursor: 'pointer', padding: 6, borderRadius: 6,
    display: 'flex', alignItems: 'center', flexShrink: 0,
  },
  main: {
    flex: 1,
    marginLeft: 240,
    minWidth: 0,
    minHeight: '100vh',
    padding: '28px 32px',
    boxSizing: 'border-box',
    background: '#f1f5f9',
    color: '#0f172a',
    '--bg': '#f1f5f9',
    '--card': '#ffffff',
    '--text': '#0f172a',
    '--muted': '#64748b',
    '--primary': '#e2e8f0',
    '--primary-strong': '#1e293b',
  },
};
